import {
  Section,
  Row,
  Column,
} from '@pearsontechnology/aero-stage-presentation-framework/dist/rce/model';
import { STAGE_ELEMENT_TYPES } from 'pluginServices/editorState/constants';
import { sanitizeProps, processBlockLevelNodesRecursively, identifyAndRegisterImagesOfElement } from './helpers';
import uuid from 'uuid/v1';
import componentService from 'services/ComponentService';
import { cloneDeep } from 'lodash';

export const copyPasteElements = async (selectedElements, currentInteractiveConfig, createComponent, screenId, editorState, registerComponentEffects) => {

  const isSection = selectedElements[0]?.type === STAGE_ELEMENT_TYPES.SECTION;

  let config = currentInteractiveConfig;
  let componentsData = [];
  const { components: interactiveConfigComponents = [] } = currentInteractiveConfig;

  const regexId = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;

  let newSection = new Section([
    new Row([
      new Column([]),
    ]),
  ]);

  if (isSection) {
    const { rows, ref, _path, stageRef, ...otherProps } = selectedElements[0];
    Object.assign(newSection, otherProps);
  }

  let newColumns = [...newSection.rows[0].columns];

  const registerPluginInsidePlugin = async (element) => {
    let componentConfig = JSON.stringify(element._snapshot);
    const componentIds = componentConfig.match(regexId) || [];

    await Promise.all(componentIds.map(async (id) => {
      if (componentService.isComponent(id)) {
        const { plugin: { pluginId: _pluginId, version: _pluginVersion }, config: _snapshot } = await componentService.getComponent(id);
        const inlineComponentData = await createComponent(screenId, { _snapshot, _pluginId, _pluginVersion });
        registerComponentEffects(inlineComponentData);

        componentConfig = componentConfig.replace(id, inlineComponentData._componentId);
      }
    }));

    return { ...element, _snapshot: JSON.parse(componentConfig) };
  };

  const updateRefsinPluginConfigs = async (data, config, pluginConfigs) => {
    for await (let node of data) {

      if (node?.data?.ref) {
        const refId = uuid();
        if (config?.pluginConfigs && config.pluginConfigs[node.data.ref]) {
          pluginConfigs[refId] = config.pluginConfigs?.[node.data.ref];
        }
        node.data.ref = refId;
      }

      if (node?.data?.itemId) {
        const { plugin: { pluginId: _pluginId, version: _pluginVersion }, config: _snapshot } = await componentService.getComponent(node.data.itemId);
        const inlineComponentData = await createComponent(screenId, { _snapshot, _pluginId, _pluginVersion });
        registerComponentEffects(inlineComponentData);

        node.data.itemId = inlineComponentData._componentId;
        const refId = uuid();
        componentsData.push({
          itemId: inlineComponentData._componentId,
          localRef: refId,
        });
      }

      if (node?.nodes) {
        await updateRefsinPluginConfigs(node.nodes, config, pluginConfigs);
      }
    }
  };

  await Promise.all(selectedElements.map(async (element) => {
    // register images with screen from stage elements -- text/image/table/components
    identifyAndRegisterImagesOfElement(element, screenId);

    if (element.type === STAGE_ELEMENT_TYPES.PLUGIN) {

      let clonedElement = await registerPluginInsidePlugin(element, createComponent, registerComponentEffects);
      clonedElement = await createComponent(screenId, clonedElement);
      registerComponentEffects(clonedElement);
      const refId = uuid();
      componentsData.push({
        itemId: clonedElement._componentId,
        localRef: refId,
      });

      clonedElement.localRef = refId;
      clonedElement.ref = refId;
      return clonedElement;
    }

    if (element.type === STAGE_ELEMENT_TYPES.TEXT) {
      let pluginConfigs = {};
      let clonedElement = cloneDeep(element);
      const nodesOfClonedElement = clonedElement?.value?.document?.nodes;

      await updateRefsinPluginConfigs(nodesOfClonedElement, clonedElement.config, pluginConfigs);

      clonedElement.ref = uuid();
      nodesOfClonedElement &&
        processBlockLevelNodesRecursively(nodesOfClonedElement);
      clonedElement.config.pluginConfigs = pluginConfigs;
      return clonedElement;
    }

    if (element.type === STAGE_ELEMENT_TYPES.TABLE) {
      let pluginConfigs = {};
      let clonedElement = cloneDeep(element);
      const nodesOfClonedElement = clonedElement?.value?.document?.nodes;

      await updateRefsinPluginConfigs(nodesOfClonedElement, clonedElement.config, pluginConfigs);

      clonedElement.ref = uuid();
      clonedElement.config.pluginConfigs = pluginConfigs;
      return clonedElement;
    }

    if (element.type !== STAGE_ELEMENT_TYPES.SECTION) {
      const refId = uuid();
      element.ref = refId;
    }

    return element;

  })).then((resolvedElements) => resolvedElements
    .filter((element) => element.type !== STAGE_ELEMENT_TYPES.SECTION)
    .sort((element1, element2) => element1._path.join("") - element2._path.join(""))
    .map((element) => {
      const stageElement = sanitizeProps(element);

      while (element._path[2] > newColumns.length - 1) {
        newColumns.push(new Column([]));
      }
      newColumns[element._path[2]].stageElements.push(stageElement);

      return element;
    }));

  newColumns = newColumns.filter((column) => column.stageElements.length !== 0);
  newSection.rows[0].columns = [...newColumns];
  newSection = JSON.parse(JSON.stringify(newSection));

  const selectedPath = editorState.selectedPath || [];
  const selectedPathLength = selectedPath.length;

  let targetSelectedPath = [];
  let targetSelectedElement = '';

  switch (true) {
    case (selectedPathLength === 0 || isSection || editorState.isMultiSelect):
      // will add a new section to the stage, if it doesn't have any selectedPath or it is section or a multiselect
      config.stage.sections.push(newSection);
      targetSelectedPath = [config.stage.sections.length - 2];
      targetSelectedElement = newSection;
      break;

    case selectedPathLength === 1:
      // will add a new row to a selected section, if a selected element is a section
      config.stage.sections[selectedPath[0]].rows.push(newSection.rows[0]);
      targetSelectedPath = [selectedPath[0], config.stage.sections[selectedPath[0]].rows.length - 1, 0, -1];
      targetSelectedElement = newColumns[0].stageElements[0];
      break;

    case selectedPathLength === 4:
      if (newColumns.length === 1) {
        // will add a selected stage elements into the stage, if the copied elements in one column, and paste them based on the copied elements path order
        const index = selectedPath[3] + 1;
        const { stageElements } = config.stage.sections[selectedPath[0]].rows[selectedPath[1]].columns[selectedPath[2]];
        stageElements.splice(index, 0, ...newColumns[0].stageElements);
        targetSelectedPath = selectedPath;
        targetSelectedElement = newColumns[0].stageElements[0];
      } else {
        // will arrange the copied stage elements based on the different paths of each element along the selected path into the stage
        let stageColumns = config.stage.sections[selectedPath[0]].rows[selectedPath[1]].columns;
        for (let index in newColumns) {
          if (stageColumns.length > index) {
            if (stageColumns[index].stageElements.length > selectedPath[3]) {
              stageColumns[index].stageElements.splice(selectedPath[3] + 1, 0, ...newColumns[index].stageElements);
            } else {
              stageColumns[index].stageElements.push(...newColumns[index].stageElements);
            }
          } else {
            stageColumns.push(newColumns[index]);
          }
        }
        config.stage.sections[selectedPath[0]].rows[selectedPath[1]].columns = stageColumns;

        // capturing the targetSelectedPath of a element that is pasted into the stage, to use it further for stage selection
        for (const [columnIndex, column] of Object.entries(stageColumns)) {
          const stageElementIndex = column.stageElements.findIndex((element) => element.ref === newColumns[0].stageElements[0].ref);

          if (stageElementIndex !== -1) {
            targetSelectedPath = [selectedPath[0], selectedPath[1], +columnIndex, stageElementIndex - 1];
            targetSelectedElement = stageColumns[columnIndex].stageElements[stageElementIndex];
            break;
          }
        }
      }
      break;

    default:
      break;
  }

  config.components = [...interactiveConfigComponents, ...componentsData];
  config.stage = JSON.parse(JSON.stringify(config.stage));

  return { config, targetSelectedPath, targetSelectedElement };
};
